<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">CPD Progress</h3>
          <p class="text-sm text-gray-500" v-if="user">
            {{ user.first_name }} {{ user.last_name }} · {{ user.email }}
          </p>
        </div>

        <!-- Deadline/completion banners -->
        <div v-if="isComplete(cpdProgress)" class="mt-4 bg-green-50 border border-green-200 text-green-800 rounded p-3">
          CPD target met. Great job! 🎉
        </div>
        <div v-else-if="cpdProgress.cpd_deadline && isDeadlinePast(cpdProgress.cpd_deadline)" class="mt-4 bg-red-50 border border-red-200 text-red-700 rounded p-3">
          CPD deadline has passed ({{ Math.abs(daysUntil(cpdProgress.cpd_deadline)) }} days ago) · {{ formatDate(cpdProgress.cpd_deadline) }}
        </div>
        <div v-else-if="cpdProgress.cpd_deadline && isDeadlineNear(cpdProgress.cpd_deadline)" class="mt-4 bg-amber-50 border border-amber-200 text-amber-800 rounded p-3">
          CPD deadline is near (in {{ daysUntil(cpdProgress.cpd_deadline) }} days) · {{ formatDate(cpdProgress.cpd_deadline) }}
        </div>
      </div>

      <div class="mt-4" v-if="error">
        <div class="bg-red-50 text-red-700 border border-red-200 rounded px-4 py-2">{{ error }}</div>
      </div>

      <div v-if="cpdProgress" class="space-y-6 mt-4">
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div class="rounded p-4 text-center bg-blue-50 border border-blue-100">
            <div class="text-blue-600 text-xs">Target</div>
            <div class="text-xl font-semibold text-blue-800">{{ cpdProgress.target }}</div>
          </div>
          <div class="rounded p-4 text-center bg-green-50 border border-green-100">
            <div class="text-green-600 text-xs">Earned</div>
            <div class="text-xl font-semibold text-green-800">{{ cpdProgress.earned }}</div>
          </div>
          <div class="rounded p-4 text-center bg-amber-50 border border-amber-100">
            <div class="text-amber-600 text-xs">Remaining</div>
            <div class="text-xl font-semibold text-amber-800">{{ cpdProgress.remaining }}</div>
          </div>
          <div class="rounded p-4 text-center bg-purple-50 border border-purple-100">
            <div class="text-purple-600 text-xs">Completed Assessments</div>
            <div class="text-xl font-semibold text-purple-800">{{ cpdProgress.completed_assessments_count }}</div>
          </div>
          <div class="rounded p-4 text-center bg-slate-50 border border-slate-100">
            <div class="text-slate-600 text-xs">CPD Deadline</div>
            <div class="text-xl font-semibold text-slate-800">{{ formatDate(cpdProgress.cpd_deadline) }}</div>
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm text-gray-700 font-medium">Progress</span>
            <span class="text-sm text-gray-600">{{ cpdProgress.progress_percentage }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded h-3 overflow-hidden">
            <div class="h-3 bg-custom-yellow" :style="{ width: (Number(cpdProgress.progress_percentage) || 0) + '%' }"></div>
          </div>
          <div class="mt-2 text-xs" :class="cpdProgress.is_target_met ? 'text-green-600' : 'text-gray-500'">
            {{ cpdProgress.is_target_met ? 'Target met' : 'Target not yet met' }}
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-semibold text-gray-800">Completed Assessments</h4>
          </div>
          <div class="relative overflow-x-auto shadow-sm border border-gray-100 rounded">
            <table class="w-full text-left text-gray-700">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-4 py-2 text-xs text-gray-500">Assessment</th>
                  <th class="px-4 py-2 text-xs text-gray-500">Score</th>
                  <th class="px-4 py-2 text-xs text-gray-500">CPD Points</th>
                  <th class="px-4 py-2 text-xs text-gray-500">Completed At</th>
                  <th class="px-4 py-2 text-xs text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ass, i) in cpdProgress.assessments" :key="i" class="border-t">
                  <td class="px-4 py-2">{{ ass.assessment_name }}</td>
                  <td class="px-4 py-2">{{ ass.score }}</td>
                  <td class="px-4 py-2">{{ ass.cpd_points }}</td>
                  <td class="px-4 py-2">{{ formatDateTime(ass.completed_at) }}</td>
                  <td class="px-4 py-2">
                    <button
                      class="px-3 py-1 text-xs font-medium text-white bg-custom-yellow rounded hover:bg-custom-yellow-dark disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="!ass.certificate_serial_number"
                      @click="viewCertificate(ass)"
                    >
                      View Certificate
                    </button>
                  </td>
                </tr>
                <tr v-if="!cpdProgress.assessments || cpdProgress.assessments.length === 0">
                  <td colspan="5" class="px-4 py-3 text-center text-gray-500">No assessments yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="!cpdProgress && !error" class="mt-6 text-gray-500">No CPD data available.</div>
    </template>
  </div>

  <!-- Certificate Popup Modal -->
  <div v-if="showCertificatePopup" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="absolute inset-0 bg-black opacity-50" @click="closeCertificatePopup">✕</div>
    <div class="relative z-50">
      <div
        id="certificateContainer"
        class="relative w-[500px] h-[700px] max-w-4xl bg-white rounded-lg shadow-lg overflow-hidden"
      >
        <div
          class="absolute inset-0 bg-no-repeat bg-cover bg-center z-0"
          :style="{ backgroundImage: backgroundImage ? `url(${backgroundImage})` : '' }"
        ></div>

        <!-- Dynamic Content -->
        <div class="absolute inset-0 z-10 flex flex-col text-black" style="top: 60px;">
          <p class="font-normal text-sm top-0 right-4 text-end items-end mr-7">
            Certificate No: {{ certNumber }}
          </p>
          <p class="text-lg text-center mt-24">{{ learnerName }}</p>
          <p class="text-lg text-center mt-16">{{ assessmentName }}</p>
          <p class="text-lg text-center mt-20">{{ cpdPoints }} CPD Points</p>
          <p class="text-lg text-center mt-20">{{ facilityName }}</p>
          <p class="text-sm absolute" style="bottom: 5.0rem; left: 4.5rem">
            {{ completionDate }}
          </p>

          <!-- Dynamic QR Code -->
          <div v-if="qrCodeDataUrl" class="absolute bottom-8 right-8 z-20">
            <img
              :src="qrCodeDataUrl"
              alt="Certificate Verification QR Code"
              class="w-20 h-20 bg-white p-1 rounded shadow-sm"
            />
            <p class="text-xs text-center mt-1 text-gray-600">Scan to verify</p>
          </div>
        </div>
      </div>
      <div class="flex justify-between">
        <button
          @click="closeCertificatePopup"
          class="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Close
        </button>
        <button
          @click="downloadCertificate"
          :disabled="isDownloadingCertificate"
          class="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isDownloadingCertificate ? "Downloading..." : "Download" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import html2canvas from "html2canvas";
import backgroundImage from "@/assets/images/certificate-background.png";

export default {
  name: "StaffCpdProgressTab",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      cpdProgress: null,
      showCertificatePopup: false,
      backgroundImage,
      certNumber: "",
      learnerName: "",
      assessmentName: "",
      cpdPoints: "",
      facilityName: "",
      completionDate: "",
      qrCodeDataUrl: null,
      isDownloadingCertificate: false,
      certificateVerificationLoading: false,
      certificateVerificationError: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
  },
  created() {
    this.fetchCpdProgress();
  },
  methods: {
    buildCertificateVerificationUrl(serialNumber) {
      if (!serialNumber) return null;
      const frontendBase =
        process.env.VUE_APP_FRONTEND_URL ||
        window.location.origin ||
        "";
      const trimmedBase = frontendBase.replace(/\/$/, "");

      // Generate URL with cert and user_id parameters
      const params = new URLSearchParams();
      params.append("cert", serialNumber);  // Use 'cert' parameter
      if (this.user?.user_id) {
        params.append("user_id", this.user.user_id);
      }

      return `${trimmedBase}/verify-certificate?${params.toString()}`;
    },
    async viewCertificate(ass) {
      if (!ass || !ass.certificate_serial_number) return;

      this.certNumber = ass.certificate_serial_number || "";
      this.certificateVerificationError = null;
      this.certificateVerificationLoading = true;

      try {
        const verification = await api.verifyCertificate(
          this.user?.user_id,
          ass.certificate_serial_number
        );

        if (!verification?.valid) {
          this.certificateVerificationError =
            "This certificate could not be verified.";
        }

        const vUser = verification?.user || {};
        const vAssessment = verification?.assessment || {};
        const vCompletion = verification?.completion || {};
        const vToken = verification?.token || {};

        // Prefer API data, fall back to local assessment/user data
        this.learnerName =
          vUser.full_name ||
          (this.user
            ? `${this.user.first_name || ""} ${
                this.user.last_name || ""
              }`.trim()
            : "");

        this.assessmentName =
          vAssessment.assessment_name || ass.assessment_name || "";

        this.cpdPoints =
          vCompletion.cpd_points_earned ??
          vToken.cpd_points ??
          ass.cpd_points ??
          "";

        this.facilityName =
          vAssessment.facility_name || ass.facility_name || "";

        this.completionDate = this.formatDate(
          vCompletion.completion_date || ass.completed_at
        );
      } catch (error) {
        console.error("Error verifying certificate", error);
        this.certificateVerificationError =
          error.message || "Failed to verify certificate.";

        // Fallback to local data so the certificate can still render
        this.learnerName = this.user
          ? `${this.user.first_name || ""} ${
              this.user.last_name || ""
            }`.trim()
          : "";
        this.assessmentName = ass.assessment_name || "";
        this.cpdPoints = ass.cpd_points || "";
        this.facilityName = ass.facility_name || "";
        this.completionDate = this.formatDate(ass.completed_at);
      } finally {
        this.certificateVerificationLoading = false;
      }

      const verificationUrl = this.buildCertificateVerificationUrl(
        ass.certificate_serial_number
      );
      // Generate a QR code image URL from the verification URL
      this.qrCodeDataUrl = verificationUrl
        ? `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(
            verificationUrl
          )}`
        : null;

      this.showCertificatePopup = true;
    },
    closeCertificatePopup() {
      this.showCertificatePopup = false;
    },
    async downloadCertificate() {
      if (this.isDownloadingCertificate) return;
      const certificateEl = document.getElementById("certificateContainer");
      if (!certificateEl) return;
      this.isDownloadingCertificate = true;
      try {
        const canvas = await html2canvas(certificateEl, {
          scale: 2,
          useCORS: true,
          backgroundColor: null,
        });
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${this.assessmentName || "certificate"}.png`;
        link.click();
      } catch (error) {
        console.error("Failed to download certificate", error);
      } finally {
        this.isDownloadingCertificate = false;
      }
    },
    async fetchCpdProgress() {
      if (!this.user || !this.user.user_id) return;
      this.loading = true;
      this.error = null;
      this.cpdProgress = null;
      try {
        const response = await api.getUserCpdProgress(this.user.user_id);
        this.cpdProgress = response.data || response;
      } catch (e) {
        this.error = e.message || "Failed to fetch CPD progress";
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {},
    formatDate(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        if (isNaN(d)) return date;
        return d.toLocaleDateString();
      } catch {
        return date;
      }
    },
    formatDateTime(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },
    daysUntil(date) {
      try {
        const now = new Date();
        const d = new Date(date);
        const diffMs = d - now;
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      } catch {
        return 0;
      }
    },
    isDeadlineNear(date) {
      const days = this.daysUntil(date);
      return days >= 0 && days <= 14; // within 14 days
    },
    isDeadlinePast(date) {
      const days = this.daysUntil(date);
      return days < 0;
    },
    isComplete(progress) {
      const pct = Number(progress?.progress_percentage);
      return !isNaN(pct) && pct >= 100;
    }
  },
};
</script>

<style lang="css" scoped></style>


